'use client'
import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useAuth } from '@/src/hooks/useAuth'
import ImageComponent from '../../common/ImageComponent'
import Link from 'next/link'
import { getBlogsPath, getCartPath, getHomePath, getWishlistPath } from '@/src/routes/routes'
import { BagSVG, ChevronDownSVG, SearchSVG, UserSVG, WishlistSVG } from '@/src/ui/AllSVG'
import { usePathname } from 'next/navigation'
import { openModal } from '@/src/store/slices/allModalSlice'
import { useDispatch } from 'react-redux'
import SearchResult from '../search/SearchResult'
import MegaMenu from './MegaMenu'
import MobileNavOffcanvas from './MobileNavOffcanvas'
import MobileBottomNav from './MobileBottomNav'
import AccountDropdown from './AccountDropdown'
import { IMegaMenuCategoriesAPIResponseData } from '@/src/types/megaMenu'
import { useGetUserCartDataQuery, useGetUserWishlistDataQuery } from '@/src/store/rtkQueries/userGetAPI'

const FALLBACK_SITE_LOGO = '/images/main/logo.png'

interface PrimaryHeaderProps {
    megaMenuData: IMegaMenuCategoriesAPIResponseData[];
    initialIsAuthenticated?: boolean;
    /** From `/user/get-global` → `logo` */
    siteLogo?: string | null;
}
export default function PrimaryHeader({ megaMenuData, initialIsAuthenticated, siteLogo }: PrimaryHeaderProps) {
    const logoSrc = siteLogo || FALLBACK_SITE_LOGO
    const { isAuthenticated } = useAuth(initialIsAuthenticated)
    const { data: cartData } = useGetUserCartDataQuery(undefined, { skip: !isAuthenticated })
    const cartCount = cartData?.data?.summary?.item_count ?? 0
    const { data: wishlistData } = useGetUserWishlistDataQuery(undefined, { skip: !isAuthenticated })
    const wishlistCount = wishlistData?.data?.length ?? 0
    const [menuOpen, setMenuOpen] = useState(false)
    const [scrolled, setScrolled] = useState(false)
    const [searchOpen, setSearchOpen] = useState(false)
    const [megaMenuOpen, setMegaMenuOpen] = useState(false)
    const [activeNavItem, setActiveNavItem] = useState<string | null>(null)
    const closeMegaMenuTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const pathname = usePathname()

    const clearCloseMegaMenuTimeout = useCallback(() => {
        if (closeMegaMenuTimeoutRef.current) {
            clearTimeout(closeMegaMenuTimeoutRef.current)
            closeMegaMenuTimeoutRef.current = null
        }
    }, [])

    const openMegaMenu = useCallback((label: string) => {
        clearCloseMegaMenuTimeout()
        setSearchOpen(false)
        setMegaMenuOpen(true)
        setActiveNavItem(label)
    }, [clearCloseMegaMenuTimeout])

    const closeMegaMenu = useCallback(() => {
        clearCloseMegaMenuTimeout()
        setMegaMenuOpen(false)
        setActiveNavItem(null)
    }, [clearCloseMegaMenuTimeout])

    const scheduleCloseMegaMenu = useCallback(() => {
        clearCloseMegaMenuTimeout()
        closeMegaMenuTimeoutRef.current = setTimeout(closeMegaMenu, 200)
    }, [clearCloseMegaMenuTimeout, closeMegaMenu])

    useEffect(() => () => clearCloseMegaMenuTimeout(), [clearCloseMegaMenuTimeout])
    const isHome = pathname === getHomePath()
    const dispatch = useDispatch()

    const NAV_ITEMS = [
        ...megaMenuData.map((item) => ({
            label: item.name,
            hasDropdown: true,
            href: undefined,
        })),
        { label: 'Stories', hasDropdown: false, href: getBlogsPath() },
    ]

    useEffect(() => {
        if (!isHome) return
        const onScroll = () => setScrolled(window.scrollY > 40)
        window.addEventListener('scroll', onScroll, { passive: true })
        return () => window.removeEventListener('scroll', onScroll)
    }, [isHome])

    const signupSiginModal = () => {
        setMenuOpen(false)
        dispatch(openModal({
            componentName: 'LoginSignupIndex',
            data: {
                componentName: 'SignIn',
            },
            modalSize: 'full',
            modalPadding: 'p-0',
        }))
    }

    const toggleMobileMenu = () => {
        setMenuOpen((prev) => {
            const next = !prev
            if (next) {
                setSearchOpen(false)
                setMegaMenuOpen(false)
                setActiveNavItem(null)
            }
            return next
        })
    }

    const openMobileSearch = () => {
        setSearchOpen(true)
        setMenuOpen(false)
        setMegaMenuOpen(false)
        setActiveNavItem(null)
    }
    return (
        <>
            <SearchResult isOpen={searchOpen} onClose={() => setSearchOpen(false)} logoSrc={logoSrc} />
            <MegaMenu
                megaMenuData={megaMenuData}
                activeNavItem={activeNavItem}
                isOpen={megaMenuOpen}
                onClose={closeMegaMenu}
                onPanelMouseEnter={clearCloseMegaMenuTimeout}
                onPanelMouseLeave={scheduleCloseMegaMenu}
            />
            <MobileNavOffcanvas
                megaMenuData={megaMenuData}
                logoSrc={logoSrc}
                isOpen={menuOpen}
                onClose={() => setMenuOpen(false)}
            />
            <MobileBottomNav
                menuOpen={menuOpen}
                searchOpen={searchOpen}
                cartCount={cartCount}
                isAuthenticated={isAuthenticated}
                onMenuToggle={toggleMobileMenu}
                onSearchOpen={openMobileSearch}
                onSignIn={signupSiginModal}
            />
            <div
                className={`relative ${isHome && !scrolled ? 'bg-transparent' : 'bg-white'} transition-[background-color,border-color,color] duration-500 ease-in-out w-full z-50 sticky top-0 border-b ${isHome && !scrolled ? 'border-secondary-background-color/10' : 'border-muted-color'}`}
                onMouseEnter={megaMenuOpen ? clearCloseMegaMenuTimeout : undefined}
                onMouseLeave={megaMenuOpen ? scheduleCloseMegaMenu : undefined}
            >
                {megaMenuOpen && (
                    <div
                        className="absolute left-0 right-0 top-full h-10 pointer-events-auto"
                        aria-hidden
                    />
                )}
                <div className="custom_container vertical_padding">
                    <div className="flex items-center justify-between">
                        {/* Logo */}
                        <Link href={getHomePath()} className="shrink-0">
                            <div className={`w-24 md:w-32 h-8 md:h-10 transition-[filter] duration-500 ease-in-out ${isHome && !scrolled ? '' : 'invert'}`}>
                                <ImageComponent src={logoSrc} alt="Logo" object_cover={false} priority={true} />
                            </div>
                        </Link>

                        {/* Desktop Nav */}
                        <nav className="hidden lg:flex items-center gap-2 xl:gap-7">
                            {NAV_ITEMS && NAV_ITEMS?.length > 0 && NAV_ITEMS?.map((item, index) => {
                                const itemClass = `py-2 flex items-center gap-1 text-[10px] xl:text-xs tracking-widest uppercase font-medium hover:opacity-70 transition-[color,opacity] duration-500 ease-in-out cursor-pointer ${isHome && !scrolled ? 'text-white' : 'text-black'}`
                                if (item?.href) {
                                    return (
                                        <Link
                                            key={`${item?.label}-${index}`}
                                            href={item?.href}
                                            className={itemClass}
                                            onMouseEnter={closeMegaMenu}
                                        >
                                            {item?.label}
                                        </Link>
                                    )
                                }
                                return (
                                    <button
                                        key={`${item?.label}-${index}`}
                                        type="button"
                                        onMouseEnter={() => {
                                            if (item?.hasDropdown) openMegaMenu(item.label)
                                        }}
                                        className={itemClass}
                                    >
                                        {item?.label}
                                        {item?.hasDropdown && (
                                            <ChevronDownSVG className={`w-3 h-3 transition-transform duration-300 ${activeNavItem === item?.label ? 'rotate-180' : ''} ${isHome && !scrolled ? 'text-white' : 'text-black'}`} />
                                        )}
                                    </button>
                                )
                            })}
                        </nav>

                        {/* Actions */}
                        <div className={`flex items-center gap-4 md:gap-6 lg:gap-8 transition-[color] duration-500 ease-in-out ${isHome && !scrolled ? 'text-white' : 'header_drak_actions'}`}>
                            <button
                                onClick={() => { setSearchOpen(true); setMegaMenuOpen(false); setActiveNavItem(null) }}
                                className="hover:opacity-70 transition-opacity cursor-pointer"
                                aria-label="Search"
                            >
                                <SearchSVG />
                            </button>

                            {isAuthenticated ?
                                <AccountDropdown isHome={isHome} scrolled={scrolled} /> :
                                (<button onClick={() => signupSiginModal()} className="hover:opacity-70 transition-opacity cursor-pointer" aria-label="Sign in">
                                    <UserSVG />
                                </button>)}

                            {isAuthenticated ? (
                                <Link href={getCartPath()} className="relative hover:opacity-70 transition-opacity cursor-pointer" aria-label="Bag">
                                    <BagSVG />
                                    {cartCount > 0 && (
                                        <span className="absolute -top-2 -right-2 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-heading-color text-white text-[10px] font-semibold leading-none">
                                            {cartCount > 99 ? '99+' : cartCount}
                                        </span>
                                    )}
                                </Link>

                            ) : (<button onClick={() => signupSiginModal()} className="hover:opacity-70 transition-opacity cursor-pointer" aria-label="Bag">
                                <BagSVG />
                            </button>)}

                            {isAuthenticated ? (
                                <Link href={getWishlistPath()} className="relative hover:opacity-70 transition-opacity cursor-pointer" aria-label="Wishlist">
                                    <WishlistSVG />
                                    {wishlistCount > 0 && (
                                        <span className="absolute -top-2 -right-2 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-heading-color text-white text-[10px] font-semibold leading-none">
                                            {wishlistCount > 99 ? '99+' : wishlistCount}
                                        </span>
                                    )}
                                </Link>
                            ) : (
                                <button onClick={() => signupSiginModal()} className="hover:opacity-70 transition-opacity cursor-pointer" aria-label="Wishlist">
                                    <WishlistSVG />
                                </button>
                            )}

                            {/* Hamburger — mobile only */}
                            <button
                                className="lg:hidden flex flex-col justify-center gap-[5px] w-6 h-6 hover:opacity-70 transition-opacity cursor-pointer"
                                aria-label="Toggle menu"
                                aria-expanded={menuOpen}
                                onClick={toggleMobileMenu}
                            >
                                <span className={`block w-full h-[1.5px] transition-[background-color,transform] duration-500 ease-in-out origin-center ${isHome && !scrolled ? 'bg-white' : 'bg-black'} ${menuOpen ? 'rotate-45 translate-y-[6.5px]' : ''}`} />
                                <span className={`block w-full h-[1.5px] transition-[background-color,opacity,transform] duration-500 ease-in-out ${isHome && !scrolled ? 'bg-white' : 'bg-black'} ${menuOpen ? 'opacity-0 scale-x-0' : ''}`} />
                                <span className={`block w-full h-[1.5px] transition-[background-color,transform] duration-500 ease-in-out origin-center ${isHome && !scrolled ? 'bg-white' : 'bg-black'} ${menuOpen ? '-rotate-45 -translate-y-[6.5px]' : ''}`} />
                            </button>
                        </div>
                    </div>
                </div>

            </div>
        </>
    )
}
